// Phase 56 — Customer Duplicate Detection & Merge — Find Duplicates page.
//
// Read-only entry surface that lists duplicate clusters returned by
// ``GET /api/customers/duplicates`` and the recent merge history from
// ``GET /api/customers/merges``. Each cluster row deep-links into the
// side-by-side merge page (Phase 56 chunk 4). Each recent-merge row
// surfaces a Restore button while inside the 30-day window.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Phone, Mail, GitMerge, Undo2, AlertTriangle } from "lucide-react";

function PageShell({ title, kicker, action, children }) {
  return (
    <div className="page" data-testid="customer-duplicates-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// {kicker}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">{title}</h1>
        </div>
        {action}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

function SignalBadge({ raw }) {
  const [kind, ...rest] = raw.split(":");
  const value = rest.join(":");
  let label = raw, tone = "bg-secondary text-foreground border-border";
  if (kind === "phone") {
    label = `Phone ${value}`;
    tone = "bg-sky-100 text-sky-800 border-sky-200";
  } else if (kind === "email") {
    label = `Email ${value}`;
    tone = "bg-violet-100 text-violet-800 border-violet-200";
  } else if (kind === "name") {
    label = `Name ${value}%`;
    tone = "bg-amber-100 text-amber-800 border-amber-200";
  }
  return (
    <span
      className={`inline-flex items-center text-[11px] font-bold mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone}`}
      data-testid={`duplicate-signal-${kind}`}
    >
      {label}
    </span>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const ms = d.getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
}

export default function CustomerDuplicates() {
  const navigate = useNavigate();
  const [clusters, setClusters] = useState([]);
  const [merges, setMerges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, m] = await Promise.all([
        api.get("/customers/duplicates"),
        api.get("/customers/merges"),
      ]);
      setClusters(d.data?.clusters || []);
      setMerges(m.data?.merges || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleReview = (cluster) => {
    const ids = cluster.customers.map((c) => c.id).join(",");
    navigate(`/customers/merge?ids=${encodeURIComponent(ids)}`);
  };

  const handleRestore = async (audit, snapshot) => {
    setRestoring(snapshot.id);
    try {
      await api.post(`/customers/${snapshot.id}/restore-merged`, {});
      toast.success(`Restored ${snapshot.company_name || snapshot.contact_name || snapshot.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRestoring(null);
    }
  };

  return (
    <PageShell
      title="Find Duplicates"
      kicker="Customers"
      action={
        <div className="flex items-center gap-2">
          <Link to="/customers">
            <Button variant="ghost" size="sm" className="gap-2" data-testid="duplicates-back">
              <ArrowLeft className="h-4 w-4" /> Customers
            </Button>
          </Link>
          <Button variant="outline" onClick={load} className="gap-2" disabled={loading} data-testid="duplicates-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      }
    >
      <ClustersSection clusters={clusters} loading={loading} onReview={handleReview} />
      <div className="h-10" />
      <MergesSection merges={merges} restoring={restoring} onRestore={handleRestore} />
    </PageShell>
  );
}

function ClustersSection({ clusters, loading, onReview }) {
  if (loading && clusters.length === 0) {
    return <div className="text-muted-foreground text-sm">Scanning customers…</div>;
  }
  if (!loading && clusters.length === 0) {
    return (
      <div
        className="bg-emerald-50 border border-emerald-200 rounded-[8px] px-6 py-8 text-center"
        data-testid="duplicates-empty-state"
      >
        <div className="text-2xl">✓</div>
        <div className="text-sm font-bold text-emerald-900 mt-2">No duplicate clusters found.</div>
        <div className="text-xs text-emerald-800/70 mt-1">
          Detection runs across phone, email, and a fuzzy company-name match (≥85% similar).
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
        // Clusters · {clusters.length}
      </div>
      <div className="space-y-3" data-testid="duplicate-clusters-list">
        {clusters.map((cl) => (
          <ClusterCard key={cl.cluster_id} cluster={cl} onReview={onReview} />
        ))}
      </div>
    </>
  );
}

function ClusterCard({ cluster, onReview }) {
  return (
    <div
      className="bg-card border border-border rounded-[8px] p-4"
      data-testid={`cluster-card-${cluster.cluster_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground mb-1">
            {cluster.size} possible duplicates
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {cluster.customers.map((c) => {
              const counts = cluster.counts_by_id?.[c.id] || {};
              return (
                <Link
                  key={c.id}
                  to={`/customers/${c.id}`}
                  className="inline-flex items-center gap-2 bg-secondary border border-border hover:border-accent rounded-full pl-2 pr-3 py-1 transition-colors group"
                  data-testid={`cluster-chip-${c.id}`}
                >
                  <span className="text-sm font-bold group-hover:text-accent transition-colors">
                    {c.company_name || c.contact_name || c.id.slice(0, 8)}
                  </span>
                  {counts.bookings > 0 && (
                    <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {counts.bookings} bk
                    </span>
                  )}
                  {counts.invoices > 0 && (
                    <span className="mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {counts.invoices} inv
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {cluster.signals.map((s) => (
              <SignalBadge key={s} raw={s} />
            ))}
          </div>
        </div>
        <Button
          onClick={() => onReview(cluster)}
          className="gap-2 shrink-0"
          data-testid={`cluster-review-${cluster.cluster_id}`}
        >
          <GitMerge className="h-4 w-4" /> Review & merge
        </Button>
      </div>
    </div>
  );
}

function MergesSection({ merges, restoring, onRestore }) {
  if (!merges.length) return null;
  return (
    <>
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
        // Recently merged · {merges.length}
      </div>
      <div className="space-y-2" data-testid="recently-merged-list">
        {merges.map((m) => (
          <MergeRow key={m.id} merge={m} restoring={restoring} onRestore={onRestore} />
        ))}
      </div>
    </>
  );
}

function MergeRow({ merge, restoring, onRestore }) {
  const survivorName =
    merge.survivor_snapshot_after?.company_name ||
    merge.survivor_snapshot_after?.contact_name ||
    merge.survivor_id?.slice(0, 8);
  const days = daysUntil(merge.restore_window_expired_at);
  const expired = days !== null && days <= 0;
  const restoredIds = new Set(merge.restored_customer_ids || []);
  return (
    <div
      className="bg-card border border-border rounded-[8px] px-4 py-3"
      data-testid={`merge-row-${merge.id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0 text-sm">
          <div>
            <span className="font-bold">{merge.merged_customer_snapshots.length}</span>{" "}
            customer{merge.merged_customer_snapshots.length === 1 ? "" : "s"} merged into{" "}
            <span className="font-bold">{survivorName}</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {formatDateTime(merge.created_at)} · {merge.by_user || "unknown"}
            {merge.reassignment_counts ? (
              <>
                {" "}· {merge.reassignment_counts.bookings || 0} bookings,{" "}
                {merge.reassignment_counts.invoices || 0} invoices reassigned
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {expired ? (
            <span
              className="inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest text-muted-foreground"
              title="The 30-day restore window has elapsed for this merge"
              data-testid={`merge-row-${merge.id}-expired`}
            >
              <AlertTriangle className="h-3 w-3" /> Window expired
            </span>
          ) : (
            <span className="mono text-[10px] uppercase tracking-widest text-emerald-700">
              {days} day{days === 1 ? "" : "s"} left
            </span>
          )}
          <div className="flex flex-wrap items-center justify-end gap-1.5 mt-1">
            {merge.merged_customer_snapshots.map((snap) => {
              const alreadyRestored = restoredIds.has(snap.id);
              return (
                <Button
                  key={snap.id}
                  size="sm"
                  variant={alreadyRestored ? "ghost" : "outline"}
                  className="gap-1"
                  disabled={expired || alreadyRestored || restoring === snap.id}
                  onClick={() => onRestore(merge, snap)}
                  data-testid={`merge-restore-${snap.id}`}
                  title={alreadyRestored ? "Already restored" : (expired ? "Window expired" : "Restore this customer")}
                >
                  <Undo2 className="h-3 w-3" />
                  {alreadyRestored ? "Restored" : `Restore ${(snap.company_name || snap.id || "").slice(0, 16)}`}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
